import React from "react";
import { useNavigate } from "react-router-dom"; // 1. Import hook
import { useDispatch } from "react-redux";
import {
  Store, User, Briefcase, FileText, MapPin, 
  CreditCard, Contact, X, BarChart3, Sparkles, Users
} from "lucide-react";
import { setMerchantId as setGlobalMerchantId } from "../../../../features/global/globalSlice";

const ShopDetailModal = ({ shop, onClose }) => {
  const navigate = useNavigate(); // 2. Khởi tạo navigate
  const dispatch = useDispatch();

  if (!shop) return null;

  // 3. Hàm xử lý chuyển trang chung
  const handleNavigate = (path) => {
    if (shop.terminalID) {
      dispatch(setGlobalMerchantId(shop.terminalID));
      navigate(path);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
              <Store size={24} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                Chi tiết Cửa hàng
              </h2>
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
                {shop.terminalID}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {/* Main Name Card */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <h3 className="text-xl font-bold text-slate-900 mb-1">
              {shop.workspaceName || "Chưa cập nhật tên"}
            </h3>
            <div className="flex items-center gap-2 text-blue-700 text-sm">
              <User size={14} />
              <span className="font-medium">{shop.userName}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Business Info Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-900 font-semibold border-b pb-2 border-slate-100">
                <Briefcase size={16} className="text-slate-500" />
                Thông tin Kinh doanh
              </div>
              <div className="space-y-3">
                <InfoItem label="Loại hình" value={shop.businessType} />
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Giấy phép ĐKKD</p>
                  <div className="flex items-center gap-2">
                    {shop.businessRegistrationNumber ? (
                      <span className="text-sm font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100 flex items-center gap-1 w-full">
                        <FileText size={12} /> {shop.businessRegistrationNumber}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">Chưa đăng ký</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-0.5">Terminal ID</p>
                  <span className="text-sm font-mono font-medium text-slate-700 bg-slate-50 px-2 py-1 rounded border border-slate-100 flex items-center gap-1 w-fit">
                    <CreditCard size={12} /> {shop.terminalID}
                  </span>
                </div>
              </div>
            </div>

            {/* Address Info Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-900 font-semibold border-b pb-2 border-slate-100">
                <MapPin size={16} className="text-slate-500" />
                Địa điểm & Liên hệ
              </div>
              <div className="space-y-3">
                <InfoBox label="Địa chỉ kinh doanh" value={shop.merchantAddress} />
                <InfoBox label="Địa chỉ chủ sở hữu" value={shop.userAddress} icon={Contact} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-end gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 mr-2">
             {/* 4. Gán sự kiện cho các nút */}
             <ActionButton 
                icon={BarChart3} 
                label="Doanh thu" 
                color="blue" 
                onClick={() => handleNavigate('/revenue')} 
             />
             <ActionButton 
                icon={Sparkles} 
                label="Dự báo" 
                color="purple" 
                onClick={() => handleNavigate('/predict')} 
             />
             <ActionButton 
                icon={Users} 
                label="Khách hàng" 
                color="indigo" 
                onClick={() => handleNavigate('/customer')} 
             />
          </div>
          <div className="h-6 w-px bg-slate-200 mx-1 hidden sm:block"></div>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors">
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-slate-700 bg-slate-50 p-2 rounded border border-slate-100">
      {value || "Chưa cập nhật"}
    </p>
  </div>
);

const InfoBox = ({ label, value, icon: Icon }) => (
  <div>
    <p className="text-xs text-slate-500 mb-0.5">{label}</p>
    <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded border border-slate-100 min-h-[3rem]">
      {Icon && <Icon size={14} className="inline mr-1 text-slate-400" />}
      {value || "---"}
    </p>
  </div>
);

// Cập nhật ActionButton để nhận prop onClick
const ActionButton = ({ icon: Icon, label, color, onClick }) => (
    <button 
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium text-slate-600 hover:text-${color}-600 hover:bg-${color}-50 rounded-lg transition-colors flex items-center gap-2`}
    >
        <Icon size={16} />
        <span className="hidden sm:inline">{label}</span>
    </button>
)

export default ShopDetailModal;
